/**
 * Fuentes a adjuntar a una ficha técnica desde el studio AI Designs.
 * Preferir PNG en sketches (SVG no encaja bien en slots del moodboard).
 *
 * @param {Array<{ relativeUrl?: string, pngRelativeUrl?: string, filename?: string, mode?: string }>} history
 * @param {Array<{ file?: File }>} refs
 * @returns {{
 *   dibujoUrls: Array<{ url: string, filename: string }>,
 *   sketchUrls: Array<{ url: string, filename: string }>,
 *   refFiles: File[],
 * }}
 */
export function collectFichaAttachSources(history = [], refs = []) {
  const dibujoUrls = [];
  const sketchUrls = [];
  const seen = new Set();

  const pushUrl = (bucket, item) => {
    const png = String(item?.pngRelativeUrl || "").trim();
    const rel = String(item?.relativeUrl || "").trim();
    const url = png || rel;
    if (!url || seen.has(url)) return;
    seen.add(url);
    const base =
      String(item?.filename || "").trim() ||
      url.split("/").pop() ||
      `design-${bucket.length + 1}.png`;
    const preferPng = Boolean(png) || item?.mode === "sketch";
    const filename =
      preferPng && !/\.png$/i.test(base)
        ? `${base.replace(/\.[^.]+$/, "") || "sketch"}.png`
        : base;
    bucket.push({ url, filename });
  };

  for (const item of Array.isArray(history) ? history : []) {
    if (item?.mode === "ref") continue; // prenda de entrada: va por refFiles / data URL
    if (item?.mode === "sketch") pushUrl(sketchUrls, item);
    else pushUrl(dibujoUrls, item);
  }

  const refFiles = (Array.isArray(refs) ? refs : [])
    .map((r) => r?.file)
    .filter((f) => f && typeof f === "object");

  return { dibujoUrls, sketchUrls, refFiles };
}

/**
 * Convierte data URLs de historial mode=ref en Files para adjuntar.
 * @param {Array<{ mode?: string, previewUrl?: string, filename?: string }>} history
 * @returns {Promise<File[]>}
 */
export async function filesFromHistoryRefs(history = []) {
  const out = [];
  for (const item of Array.isArray(history) ? history : []) {
    if (item?.mode !== "ref") continue;
    const dataUrl = String(item?.previewUrl || "").trim();
    if (!dataUrl.startsWith("data:")) continue;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const name =
        String(item?.filename || "").trim() || `ref-${out.length + 1}.png`;
      out.push(
        new File([blob], name, {
          type: blob.type || "image/png",
        }),
      );
    } catch {
      /* skip */
    }
  }
  return out;
}

/**
 * PagesJson del API: array, string JSON, o documento { pages }.
 * @param {*} raw
 * @returns {Array}
 */
export function coerceMoodboardPages(raw) {
  if (raw == null || raw === "") return [];
  let parsed = raw;
  if (typeof raw === "string") {
    const t = String(raw).trim();
    if (!t) return [];
    try {
      parsed = JSON.parse(t);
    } catch {
      return [];
    }
  }
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object" && Array.isArray(parsed.pages)) {
    return parsed.pages;
  }
  return [];
}

/**
 * Añade assets nuevos a la mesa libre del moodboard (página 0).
 * No toca slots ya colocados; evita duplicar el mismo assetId.
 *
 * @param {Array} pages
 * @param {number[]} assetIds
 * @param {{ newSlotId?: (prefix?: string) => string, clampSlot?: (raw: object) => object }} helpers
 * @returns {{ pages: Array, added: number }}
 */
export function placeMoodboardAssetsOnPages(
  pages = [],
  assetIds = [],
  helpers = {},
) {
  const newSlotId =
    typeof helpers.newSlotId === "function"
      ? helpers.newSlotId
      : (prefix = "ai") =>
          `${prefix}-${Date.now().toString(36)}-${Math.random()
            .toString(36)
            .slice(2, 7)}`;
  const clampSlot =
    typeof helpers.clampSlot === "function" ? helpers.clampSlot : (raw) => raw;

  const ids = (assetIds || [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const empty = Array.isArray(pages) ? pages : [];
  if (!ids.length) return { pages: empty, added: 0 };

  const list =
    Array.isArray(pages) && pages.length
      ? pages.map((p) => ({
          ...p,
          slots: [...(p?.slots || [])],
          marks: [...(p?.marks || [])],
        }))
      : [{ slots: [], marks: [] }];
  const already = new Set();
  for (const page of list) {
    for (const slot of page?.slots || []) {
      const aid = Number(slot?.assetId);
      if (Number.isFinite(aid) && aid > 0) already.add(aid);
    }
  }
  const pending = ids.filter((id) => !already.has(id));
  if (!pending.length) return { pages: list, added: 0 };

  const pageIdx = 0;
  const page = list[pageIdx] || { slots: [], marks: [] };
  const slots = [...(page.slots || [])];
  let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
  const slotW = 0.28;
  const slotH = 0.26;
  const baseCount = slots.length;

  pending.forEach((assetId, i) => {
    maxZ += 1;
    const n = baseCount + i;
    const col = n % 3;
    const row = Math.floor(n / 3);
    const x = Math.min(1 - slotW, Math.max(0, 0.08 + col * 0.3));
    const y = Math.min(1 - slotH, Math.max(0, 0.08 + row * 0.28));
    slots.push(
      clampSlot({
        id: newSlotId("ai"),
        x,
        y,
        w: slotW,
        h: slotH,
        shape: "rect",
        z: maxZ,
        assetId,
      }),
    );
  });

  list[pageIdx] = { ...page, slots };
  return { pages: list, added: pending.length };
}

/**
 * Rellena slots vacíos por página sin tocar marks ni el layout:
 * 0 Dibujos, 1 Siluetas/sketches, 2 Trimmings/medidas.
 *
 * @param {Array} pages
 * @param {{ dibujoIds?: number[], sketchIds?: number[], medidasIds?: number[] }} groups
 */
export function placeFichaAssetsOnPages(pages = [], groups = {}) {
  const dibujoIds = (groups.dibujoIds || [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const sketchIds = (groups.sketchIds || [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const medidasIds = (groups.medidasIds || [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);

  const fill = (page, ids) => {
    if (!page || !Array.isArray(page.slots) || !ids.length) return page;
    let i = 0;
    const slots = page.slots.map((slot) => {
      if (slot?.assetId != null && Number(slot.assetId) > 0) return slot;
      if (i >= ids.length) return slot;
      const assetId = ids[i++];
      return { ...slot, assetId };
    });
    return { ...page, slots };
  };

  const list = Array.isArray(pages) ? pages : [];
  return list.map((page, idx) => {
    if (idx === 0) return fill(page, dibujoIds);
    if (idx === 1) return fill(page, sketchIds);
    if (idx === 2) return fill(page, medidasIds);
    return page;
  });
}
